import React from "react";
import { useNavigate } from "react-router-dom";

import { Button, Container, Icon, Modal } from "semantic-ui-react";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { NAV } from "../../navLabels";
import { useGetAllowancesQuery } from "./allowancesApiSlice";

/**
 * Entry point for 가상 고객과 롤플레이: 사전 연습 (practice, never graded) or
 * 롤플레이 (graded). Both lead to a persona choice.
 *
 * Copy and structure are the client's, from UI UX_수정요청서_v1.0 requests 3-5. The notes
 * are the notice each box has to carry, including the time limits, so a trainee reads the
 * rules before committing rather than discovering them mid-session.
 */

const MODES = [
  {
    key: "practice",
    icon: "clipboard outline",
    title: "사전 연습하기",
    to: "/simulation/practice",
    accent: "#34C759",
    notes: [
      "롤플레이를 시작하기 전 사전 연습을 할 수 있습니다.",
      "사전 연습은 횟수 관계없이 인당 최대 40분(누적) 동안 할 수 있습니다.",
    ],
  },
  {
    key: "graded",
    icon: "clipboard check",
    title: "롤플레이 평가 응시",
    callout: "평가 시작 전 사전 연습을 통해 충분히 숙지하신 후 응시해 주세요.",
    to: "/customerpersonascards",
    accent: "#007AFF",
    // The quota rule mirrors api.utils.practice_budget.graded_attempts_used (an attempt is
    // spent at MIN_CHATLOGS = 6 lines, evaluated or not) and canEvaluate in constants.js.
    // The count comes from the API; exempt users (admins) get no `graded` key.
    notes: (graded) => [
      "예상 소요 시간은 약 15분~20분입니다. 카메라를 켜고 오디오가 녹음되니 비교적 조용한 공간에서 진행해 주세요.",
      "롤플레이는 25분 안에 종료되어야 합니다.",
      graded
        ? `본 평가는 ${graded.attempts_total === 1 ? "1회만" : `${graded.attempts_total}회까지`} 응시 가능하며, 결과가 평가 점수로 기록됩니다.`
        : "본 평가는 응시 횟수가 정해져 있으며, 결과가 평가 점수로 기록됩니다.",
      "대화가 발화 6개 이상(직원과 고객이 3번 이상 주고받기) 이어지면, 평가 받기를 누르지 않아도 1회로 차감됩니다. 그 전에 끝난 세션은 차감되지 않습니다.",
      "평가는 발화 6개 이상인 세션에서, 세션당 한 번 받을 수 있습니다.",
      "원하는 고객을 선택해 롤플레이를 시작합니다.",
    ],
  },
];

/**
 * Turns the API's allowance payload into what a box needs to render: a balance line, and
 * whether entry is blocked.
 *
 * Practice and graded differ deliberately (#85, restated in #112): a partial practice
 * balance is still worth using, so three minutes left means a three-minute session rather
 * than a refusal. Only exactly zero blocks. A spent graded quota has nothing to offer, so
 * any exhaustion blocks.
 */
const balanceFor = (mode, allowances) => {
  if (!allowances) return null;
  const data = allowances[mode.key];
  if (!data) return null; // no limit for this user (graded quota exempts admins)

  if (mode.key === "practice") {
    return {
      label: `남은 시간 ${data.minutes_remaining}분 / ${data.minutes_total}분`,
      blocked: data.exhausted,
      blockedText: "사전 연습 시간을 모두 사용했습니다.",
      resetsAt: data.resets_at,
    };
  }
  // Graded: the remaining count (shown since 2026-10-20; it was hidden at launch, #229).
  return {
    label: `남은 횟수 ${data.attempts_remaining}회 / ${data.attempts_total}회`,
    blocked: data.exhausted,
    blockedText: "롤플레이 횟수를 모두 사용했습니다.",
    resetsAt: data.resets_at,
  };
};

const SessionModeSelect = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  // Advisory only. The socket still refuses an exhausted budget, so a failed request here
  // must not stop a trainee entering — it just means no balance is shown.
  const { data: allowances } = useGetAllowancesQuery();
  // Graded attempts are scarce (1 by default), so entering 롤플레이 asks first and points at
  // 사전 연습. Client copy, 2026-10-20. Exempt users (no `graded` key) go straight in.
  const [gradedConfirmOpen, setGradedConfirmOpen] = React.useState(false);
  const graded = allowances?.graded;

  // Both modes now lead to a persona choice, so this screen no longer creates sessions
  // and does not need the persona list — it only routes.
  return (
    // 50px is the navbar spacer Layout renders above every page — subtracting it keeps
    // the page exactly one viewport tall instead of forcing a 50px scrollbar.
    <div
      style={{
        backgroundColor: "#f9fafb",
        minHeight: "calc(100vh - 50px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "16px 0",
          flexShrink: 0,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0,0,0,0.05)",
          marginBottom: "20px",
        }}
      >
        {/* Semantic's Container, the same as 롤플레이's header uses. A flat 24px
            gutter started the title at x=24 on desktop while that page started at x=237,
            so the two screens in the same flow looked like different apps. Container is
            safe *here* because it has no inline width:100% — that combination is what
            overflowed the card grids below (see the comments there). */}
        <Container>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "#F2F2F7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="conversation" style={{ margin: 0, color: "#007AFF", fontSize: "18px" }} />
            </div>
            <div>
              <h2 style={{ fontSize: "17px", fontWeight: "600", margin: 0, color: "#1d1d1f" }}>
                {NAV.ROLEPLAY}
              </h2>
              <p style={{ fontSize: "13px", color: "#86868b", margin: 0 }}>
                진행 방식을 선택해 주세요.
              </p>
            </div>
          </div>
        </Container>
      </div>

      {/* flex:1 + centred, so the boxes sit in the middle of the space left below the
          header rather than hugging the top. The bottom bias is desktop-only: on mobile
          the boxes stack and already fill the screen, and the resulting scrollbar eats the
          right-hand gutter, making the left margin look wider than the right. */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          padding: isMobile ? "4px 0 16px" : "24px 0 clamp(40px, 10vh, 120px)",
        }}
      >
        {/* Plain div, not Semantic's Container: `.ui.container` carries a mobile rule
            (margin: 0 1em) that combines with width:100% to overflow by 2em and shove the
            content right — the boxes ran off the right edge on narrow screens. The app
            globally overrides .ui.container's max-width to 100% anyway, so it was
            contributing nothing but that bug. */}
        <div style={{ width: "100%", padding: "0 24px" }}>
          {/* The cap lives here, not on Container: a global
              `.ui.container { max-width: 100% !important }` makes every container in the
              app full-bleed and beats any inline max-width. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: isMobile ? "14px" : "24px",
              maxWidth: "920px",
              margin: "0 auto",
            }}
          >
            {MODES.map((mode) => {
              const balance = balanceFor(mode, allowances);
              const blocked = !!balance?.blocked;
              const notes = typeof mode.notes === "function" ? mode.notes(allowances?.graded) : mode.notes;
              const enter = () => {
                if (blocked) return;
                if (mode.key === "graded" && graded) setGradedConfirmOpen(true);
                else navigate(mode.to);
              };
              return (
              <div
                key={mode.key}
                role="button"
                tabIndex={blocked ? -1 : 0}
                aria-disabled={blocked}
                onClick={enter}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    enter();
                  }
                }}
                style={{
                  cursor: blocked ? "not-allowed" : "pointer",
                  opacity: blocked ? 0.55 : 1,
                  backgroundColor: "white",
                  borderRadius: "24px",
                  padding: isMobile ? "20px 20px" : "32px 28px",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid rgba(0,0,0,0.05)",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
                  transition: "transform 0.15s ease, box-shadow 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (blocked) return; // no affordance for something that cannot be clicked
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "none";
                  e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.03)";
                }}
              >
                <div
                  style={{
                    width: isMobile ? "40px" : "56px",
                    height: isMobile ? "40px" : "56px",
                    borderRadius: "50%",
                    backgroundColor: `${mode.accent}14`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: isMobile ? "10px" : "16px",
                    flexShrink: 0,
                  }}
                >
                  <Icon
                    name={mode.icon}
                    style={{ margin: 0, color: mode.accent, fontSize: isMobile ? "18px" : "24px", lineHeight: 1 }}
                  />
                </div>

                <h3 style={{ margin: 0, fontSize: isMobile ? "17px" : "20px", fontWeight: 700, color: "#1d1d1f" }}>
                  {mode.title}
                </h3>

                {/* The trainee's actual balance, which used to arrive only after the socket
                    opened. The static note below still states the rule; this states where
                    they stand against it. */}
                {balance && (
                  <div
                    style={{
                      marginTop: "8px",
                      display: "inline-flex",
                      alignItems: "center",
                      alignSelf: "flex-start",
                      gap: "6px",
                      padding: "5px 10px",
                      borderRadius: "999px",
                      backgroundColor: blocked ? "#FFE5E5" : `${mode.accent}14`,
                      color: blocked ? "#C0392B" : "#1d1d1f",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    <Icon
                      name={blocked ? "ban" : mode.key === "graded" ? "check circle outline" : "clock outline"}
                      style={{ margin: 0, fontSize: "12px", lineHeight: 1 }}
                    />
                    {blocked ? balance.blockedText : balance.label}
                  </div>
                )}
                {blocked && balance.resetsAt && mode.key !== "graded" && (
                  <div style={{ marginTop: "6px", fontSize: "12px", color: "#86868b" }}>
                    {new Date(balance.resetsAt).toLocaleDateString("ko-KR")}에 초기화됩니다.
                  </div>
                )}

                {/* Both modes: speaker audio leaking back into the mic is transcribed as the
                    trainee or cuts the customer off (2026-10-20). Browser echo cancellation
                    catches most of it; earphones remove it. */}
                <div
                  style={{
                    marginTop: "10px",
                    padding: "8px 12px",
                    borderRadius: "10px",
                    backgroundColor: "#EAF2FF",
                    color: "#0B4FB3",
                    fontSize: isMobile ? "12.5px" : "13px",
                    fontWeight: 600,
                    display: "flex",
                    gap: "6px",
                    alignItems: "flex-start",
                  }}
                >
                  <Icon name="headphones" style={{ margin: 0 }} />
                  <span>
                    이어폰 사용을 권장합니다.
                    <span style={{ fontWeight: 400, display: "block" }}>
                      스피커 소리가 마이크에 다시 들어가면 AI가 직원의 말로 잘못 인식할 수 있습니다.
                    </span>
                  </span>
                </div>

                {mode.callout && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "8px 12px",
                      borderRadius: "10px",
                      backgroundColor: "#FFF4E5",
                      color: "#8A4B00",
                      fontSize: isMobile ? "12.5px" : "13px",
                      fontWeight: 600,
                      display: "flex",
                      gap: "6px",
                      alignItems: "flex-start",
                    }}
                  >
                    <Icon name="info circle" style={{ margin: 0 }} />
                    {mode.callout}
                  </div>
                )}

                {/* Numbered rather than bulleted: the client's copy is numbered, and the
                    ordering carries meaning (what it is, then how long, then how to start). */}
                <ol
                  style={{
                    margin: isMobile ? "10px 0 0" : "14px 0 0",
                    paddingLeft: "18px",
                    fontSize: isMobile ? "12.5px" : "13px",
                    lineHeight: isMobile ? 1.5 : 1.6,
                    color: "#424245",
                  }}
                >
                  {notes.map((note, i) => (
                    <li key={i} style={{ marginBottom: i === notes.length - 1 ? 0 : (isMobile ? "4px" : "6px") }}>
                      {note}
                    </li>
                  ))}
                </ol>
              </div>
              );
            })}
          </div>
        </div>
      </div>
      <Modal size="tiny" open={gradedConfirmOpen} onClose={() => setGradedConfirmOpen(false)}>
        <Modal.Header>롤플레이 평가 안내</Modal.Header>
        <Modal.Content>
          <p style={{ fontSize: "15px", fontWeight: 600, marginBottom: "8px" }}>
            본 평가는 {graded?.attempts_total === 1 ? "1회만" : `${graded?.attempts_total}회까지`} 응시 가능합니다.
            {graded && graded.attempts_total > 1 && ` (남은 횟수 ${graded.attempts_remaining}회)`}
          </p>
          <p style={{ marginBottom: "8px" }}>평가 시작 전 사전 연습을 통해 충분히 숙지하신 후 응시해 주세요.</p>
          <p style={{ color: "#86868b", fontSize: "13px" }}>
            대화가 발화 6개 이상 이어지면 평가 받기를 누르지 않아도 응시한 것으로 처리됩니다.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => navigate("/simulation/practice")}>사전 연습하기</Button>
          <Button primary onClick={() => navigate("/customerpersonascards")}>평가 시작</Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default SessionModeSelect;
